import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Loader2, ImageOff } from 'lucide-react';
import {
  websiteContentService,
  type WebsiteLibraryImageItem,
} from '../../services/websiteContentService';
import FullBlockingModal from '../common/FullBlockingModal';

type WebsiteImageLibraryModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onPick: (publicUrl: string) => void;
};

export default function WebsiteImageLibraryModal({
  isOpen,
  onClose,
  onPick,
}: WebsiteImageLibraryModalProps) {
  const { t } = useTranslation('admin');

  const [items, setItems] = useState<WebsiteLibraryImageItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const list = await websiteContentService.listWebsiteAdminLibraryImages();
      setItems(list);
    } catch (e: unknown) {
      setLoadError(e instanceof Error ? e.message : String(e));
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    void load();
  }, [isOpen, load]);

  if (!isOpen) return null;

  return (
    <FullBlockingModal
      className="z-[9998] p-4"
      innerClassName="w-full max-w-3xl max-h-[85vh] !p-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl"
      ariaLabelledBy="website-image-library-title"
      onBackdropClick={onClose}
    >
      <div className="flex max-h-[85vh] w-full flex-col overflow-hidden">
        <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
          <div>
            <h2 id="website-image-library-title" className="text-lg font-bold text-gray-900">
              {t('websiteContent.image_library')}
            </h2>
            <p className="text-xs text-gray-500">{t('websiteContent.image_library_hint')}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
            aria-label={t('blogAdmin.cancel')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="min-h-[200px] flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-gray-500">
              <Loader2 className="h-10 w-10 animate-spin text-primary" />
              <span className="text-sm font-medium">{t('loading')}</span>
            </div>
          )}

          {!loading && loadError && (
            <div className="rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-800">
              {loadError}
            </div>
          )}

          {!loading && !loadError && items.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-3 py-16 text-center text-gray-500">
              <ImageOff className="h-12 w-12 text-gray-300" />
              <p className="max-w-sm text-sm">{t('websiteContent.image_library_empty')}</p>
            </div>
          )}

          {!loading && !loadError && items.length > 0 && (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {items.map(item => (
                <li key={item.path}>
                  <button
                    type="button"
                    onClick={() => {
                      onPick(item.publicUrl);
                    }}
                    className="group flex w-full flex-col overflow-hidden rounded-xl border border-gray-200 bg-gray-50 text-left transition-all hover:border-primary hover:ring-2 hover:ring-primary/20"
                  >
                    <div className="aspect-square w-full overflow-hidden bg-gray-100">
                      <img
                        src={item.publicUrl}
                        alt=""
                        className="h-full w-full object-cover transition-transform group-hover:scale-105"
                        loading="lazy"
                      />
                    </div>
                    <span className="truncate px-2 py-1.5 text-[10px] font-mono text-gray-500">
                      {item.fileName}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </FullBlockingModal>
  );
}
